<template>
  <Box size="small" variant="alt-light" class="box--bordered">
    <Media align="middle" size="tiny">
      <MediaLeft v-if="draggable">
        <Btn variant="bare" class="handle">
          <Icon id="drag" />
        </Btn>
      </MediaLeft>

      <MediaBody>
        <ValidationObserver ref="form">
          <Form @passed="submit">
            <div class="directory-entry">
              <FormGroup :rules="{ required: isInteractive }">
                <CallableUsers
                  v-model="localEntry.user"
                  :name="fields.user.name"
                  :placeholder="fields.user.placeholder"
                  :disabled="!isInteractive"
                />
              </FormGroup>

              <FormGroup>
                <Input
                  v-model="localEntry.description"
                  :name="fields.description.name"
                  :placeholder="fields.description.placeholder"
                  :disabled="!isInteractive"
                />
              </FormGroup>

              <ListInline
                size="tiny"
                :class="[
                  'directory-entry-actions justify-content-end',
                  { 'is-interactive': !isInteractive },
                ]"
              >
                <template v-if="isInteractive && !forceInteractivity">
                  <li>
                    <Btn type="submit" variant="primary">
                      <span class="visible-sr">
                        {{ $t('common.add') }}
                      </span>
                    </Btn>
                  </li>
                </template>

                <template v-else-if="isInteractive && forceInteractivity">
                  <li>
                    <Btn @click="cancel">
                      {{ $t('common.cancel') }}
                    </Btn>
                  </li>
                  <li>
                    <Btn type="submit" variant="primary">
                      {{ $t('common.save') }}
                    </Btn>
                  </li>
                </template>

                <template v-else>
                  <li>
                    <Btn
                      variant="outline"
                      @click="forceInteractivity = !forceInteractivity"
                    >
                      {{ $t('common.edit') }}
                    </Btn>
                  </li>
                  <li>
                    <Btn variant="danger" @click="deleteEntry">
                      {{ $t('common.delete') }}
                    </Btn>
                  </li>
                </template>
              </ListInline>
            </div>
          </Form>
        </ValidationObserver>
      </MediaBody>
    </Media>
  </Box>
</template>

<script>
import Vue from 'vue';
import { mapActions } from 'vuex';
import _ from 'lodash';
import { ValidationObserver } from 'vee-validate';

import CallableUsers from '@/components/callable-users/CallableUsers';

export default {
  components: {
    ValidationObserver,
    CallableUsers,
  },

  props: {
    directory: {
      type: Object,
      default: () => {},
    },
    entry: {
      type: Object,
      default: () => {},
    },
    draggable: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      forceInteractivity: false,
      localEntry: {},
    };
  },

  computed: {
    fields() {
      return {
        user: {
          name: _.uniqueId('user'),
          placeholder: this.isInteractive
            ? this.$t('directories.directory.form.user.label')
            : '',
        },
        description: {
          name: _.uniqueId('description'),
          placeholder: this.isInteractive
            ? this.$t('directories.directory.form.description.label')
            : '',
        },
      };
    },

    isInteractive() {
      return this.forceInteractivity || !this.entry;
    },
  },

  watch: {
    entry: {
      handler(entry) {
        if (entry) this.localEntry = JSON.parse(JSON.stringify(entry));
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions('directories', [
      'addDirectoryEntry',
      'updateDirectoryEntry',
      'deleteDirectoryEntry',
    ]),

    cancel() {
      this.localEntry.user = this.entry.user;
      this.localEntry.description = this.entry.description;

      this.forceInteractivity = !this.forceInteractivity;
    },

    submit() {
      const event = this.localEntry.id ? this.updateEntry() : this.addEntry();
    },

    addEntry() {
      this.addDirectoryEntry({
        directory: this.directory,
        entry: this.localEntry,
      })
        .then(() => {
          this.localEntry.user = null;
          this.localEntry.description = null;
          this.$refs.form?.reset();
        })
        .catch(error => {
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: this.$t('directories.directory.error.not-added'),
          });
        });
    },

    updateEntry() {
      const entry = JSON.parse(JSON.stringify(this.entry));

      this.updateDirectoryEntry({
        directory: this.directory,
        entry: {
          ...this.localEntry,
          username: this.localEntry.user.username,
        },
      })
        .catch(error => {
          this.localEntry = entry;
          this.$notify({
            type: 'error',
            title: this.$t('common.error'),
            text: this.$t('directories.directory.error.not-updated'),
          });
        })
        .finally(() => {
          this.forceInteractivity = !this.forceInteractivity;
        });
    },

    deleteEntry(entry) {
      this.deleteDirectoryEntry({
        directory: this.directory,
        entry: this.localEntry,
      }).catch(error => {
        this.$notify({
          type: 'error',
          title: this.$t('common.error'),
          text: this.$t('directories.directory.error.not-deleted'),
        });
      });
    },
  },
};
</script>

<style lang="scss">
.directory-entry {
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  align-items: flex-start;

  flex: 1 0 auto;

  .field:disabled,
  .vs--disabled,
  .vs--disabled .vs__selected-options {
    background-image: none;
    background-color: transparent;
    border-color: transparent;
  }

  .vs--disabled .vs__search {
    display: none;
  }
}

.directory-entry-actions {
  &.is-interactive {
    .btn {
      opacity: 0;
      transition: opacity 0.15s ease;

      .directory-entry:hover & {
        opacity: 1;
      }
    }
  }
}
</style>
